import { repeatBriefingDueDay } from '@railroute/contracts/core';
import type {
  ClearanceCategory,
  ClearanceType,
  CreateClearanceDto,
  ElectricalGroup,
  UpdateClearanceDto,
  CompleteClearanceBasisDto,
} from '../api';
import type { CalendarDay } from './personnel-rows';

/**
 * Форма ввода, продления и исправления допуска в панели (issue #524, #525):
 * что в ней заполнено, чего не хватает и что уходит на сервер. Здесь же —
 * обязательная причина аннулирования: вопрос «почему запись перестаёт давать
 * допуск» из того же ряда, что «почему её исправили».
 *
 * ПРАВИЛА ВВОДА ЗДЕСЬ НЕ ЖИВУТ, ЗДЕСЬ ИХ ПОВТОРЕНИЕ. Настоящие — на сервере
 * (`apps/api/src/clearances/clearance-entry-rules.ts`), и последнее слово за
 * ним: его отказ показывается рядом с формой текстом сервера, а не
 * пересказывается. Повторение нужно ровно затем, чтобы человек увидел, чего не
 * хватает, до отправки, а не после отказа. Поэтому оно и НЕ строже серверного:
 * форма не запрещает ничего сверх того, что запретит сервер, — запретив
 * лишнее, панель отняла бы работу, которую сервер бы принял.
 *
 * Отдельным модулем от разметки, потому что проверяется: компонентных тестов в
 * окружении панели нет, а обязательные поля и окно дат — это то, на чём
 * спотыкается тот, кто вносит документ.
 */

/** Черновик формы: всё строками, как их держат поля ввода. */
export type ClearanceEntryDraft = {
  /** `OrganizationClearanceType.id`; `null` — вид ещё не выбран. */
  clearanceTypeId: string | null;
  documentNumber: string;
  /** Дата документа, `ГГГГ-ММ-ДД`. */
  documentDate: string;
  /** Срок действия, `ГГГГ-ММ-ДД`; пусто — бессрочно. */
  expiresAt: string;
  /**
   * Срок правили руками. Подстановка по периодичности вида после этого
   * молчать не станет: человек уже сказал, какой срок у документа, и
   * переписывать его при следующей правке даты значило бы спорить с ним.
   */
  expiresAtEdited: boolean;
  issuedBy: string;
  /** Группа по электробезопасности, «2».. «5»; пусто — не указана. */
  electricalSafetyGroup: string;
  notes: string;
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

/** `ГГГГ-ММ-ДД` → полночь UTC. Только для календарной арифметики внутри модуля. */
function toUtc(value: string): number {
  return Date.parse(`${value}T00:00:00.000Z`);
}

function fromUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Календарный день того, кто смотрит, → `ГГГГ-ММ-ДД` для полей ввода. */
export function isoDay(day: CalendarDay): string {
  return `${day.year}-${String(day.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
}

/** Дата плюс столько-то суток, календарём, без пояса. */
export function addDays(date: string, days: number): string {
  if (!DATE_RE.test(date)) return '';
  const base = toUtc(date);
  return Number.isNaN(base) ? '' : fromUtc(base + days * MS_PER_DAY);
}

/**
 * Срок действия по периодичности вида: дата документа плюс срок вида.
 *
 * Пусто — у бессрочного вида (периодичности нет) и пока дата документа не
 * введена. Подставленный срок можно поправить руками: периодичность — обычай
 * организации, а в самом документе может стоять другая дата.
 */
export function expiryFromPeriod(documentDate: string, type: ClearanceType | null): string {
  // Повторный инструктаж — «дата + 6 календарных месяцев», как его ставит
  // журнал, а не «+180 суток» периодичности вида (ручная проверка PR #592).
  if (type?.code === 'BRIEFING_REPEAT' && DATE_RE.test(documentDate)) return repeatBriefingDueDay(documentDate);
  const period = type?.renewalPeriodDays ?? null;
  if (period === null || period <= 0) return '';
  return addDays(documentDate, period);
}

/**
 * Спрашивает ли допуск ЭТОЙ категории номер группы по электробезопасности.
 *
 * По категории, а не по виду: исправляют и запись вида, который организация у
 * себя уже отключила, — вида среди действующих нет, а группа в документе
 * есть, и поле для неё обязано остаться.
 */
export function isElectricalCategory(category: ClearanceCategory | null | undefined): boolean {
  return category === 'ELECTRICAL_SAFETY';
}

/** Спрашивает ли этот вид номер группы по электробезопасности. */
export function isElectricalType(type: ClearanceType | null): boolean {
  return isElectricalCategory(type?.category);
}

export function emptyEntryDraft(): ClearanceEntryDraft {
  return {
    clearanceTypeId: null,
    documentNumber: '',
    documentDate: '',
    expiresAt: '',
    expiresAtEdited: false,
    issuedBy: '',
    electricalSafetyGroup: '',
    notes: '',
  };
}

/**
 * Черновик продления: вид допуска тот же и не меняется, «кто выдал» — из
 * прежней записи, номер и дата пустые.
 *
 * Пустые намеренно: продление удостоверяется НОВЫМ документом, и подставить
 * туда номер и дату прежнего значило бы предложить продлить допуск бумагой,
 * которая уже истекла.
 */
export function renewalDraft(previous: { clearanceTypeId: string; issuedBy: string }): ClearanceEntryDraft {
  return {
    ...emptyEntryDraft(),
    clearanceTypeId: previous.clearanceTypeId,
    issuedBy: previous.issuedBy,
  };
}

/**
 * Запись допуска в том объёме, в каком её правит исправление (issue #525).
 *
 * Вида допуска и работника здесь нет намеренно: их исправлением не меняют.
 * Поменять вид — значит сказать, что документ удостоверяет другое, а это уже
 * не опечатка, а другой документ, и вводят его заново.
 */
export type CorrectableClearance = {
  clearanceTypeId: string;
  /** Категория — из самой записи: по ней видно, нужна ли группа по ЭБ. */
  category: ClearanceCategory | undefined;
  documentNumber: string | null | undefined;
  documentDate: string;
  expiresAt: string | null | undefined;
  issuedBy: string;
  electricalSafetyGroup: ElectricalGroup | null | undefined;
  notes: string | null | undefined;
};

/** Группа по ЭБ в записи — римская; поле формы держит её числом, как ждёт сервер. */
const GROUP_TO_INPUT: Record<ElectricalGroup, string> = { II: '2', III: '3', IV: '4', V: '5' };

/**
 * Черновик исправления: текущие значения записи целиком (issue #525).
 *
 * `expiresAtEdited: true` с самого начала — срок в черновике взят из ДОКУМЕНТА,
 * а не подставлен по периодичности вида. Без этого правка даты документа
 * молча переписала бы срок по обычаю организации, то есть исправление одной
 * опечатки завело бы вторую.
 */
export function correctionDraft(record: CorrectableClearance): ClearanceEntryDraft {
  return {
    clearanceTypeId: record.clearanceTypeId,
    documentNumber: record.documentNumber ?? '',
    documentDate: record.documentDate.slice(0, 10),
    expiresAt: record.expiresAt ? record.expiresAt.slice(0, 10) : '',
    expiresAtEdited: true,
    issuedBy: record.issuedBy,
    electricalSafetyGroup: record.electricalSafetyGroup ? GROUP_TO_INPUT[record.electricalSafetyGroup] : '',
    notes: record.notes ?? '',
  };
}

/** Выбран вид допуска: срок пересчитывается, группа чистится у неэлектрических. */
export function withType(draft: ClearanceEntryDraft, type: ClearanceType | null): ClearanceEntryDraft {
  const next: ClearanceEntryDraft = { ...draft, clearanceTypeId: type?.id ?? null };
  if (!isElectricalType(type)) next.electricalSafetyGroup = '';
  if (!next.expiresAtEdited) next.expiresAt = expiryFromPeriod(next.documentDate, type);
  return next;
}

/** Введена дата документа: срок пересчитывается, пока его не правили руками. */
export function withDocumentDate(
  draft: ClearanceEntryDraft,
  documentDate: string,
  type: ClearanceType | null,
): ClearanceEntryDraft {
  const next: ClearanceEntryDraft = { ...draft, documentDate };
  if (!next.expiresAtEdited) next.expiresAt = expiryFromPeriod(documentDate, type);
  return next;
}

/** Срок правили руками — подстановка по периодичности больше его не трогает. */
export function withExpiresAt(draft: ClearanceEntryDraft, expiresAt: string): ClearanceEntryDraft {
  return { ...draft, expiresAt, expiresAtEdited: true };
}

/** «30 дней», «31 дня», «1 дня» — родительный падеж после «старше»/«не старше». */
export function daysGenitive(n: number): string {
  return `${n} ${n % 10 === 1 && n % 100 !== 11 ? 'дня' : 'дней'}`;
}

/** Подсказка под полем даты документа: насколько старым он может быть. */
export function documentDateHint(maxAgeDays: number): string {
  return maxAgeDays === 0 ? 'только сегодняшней датой' : `не старше ${daysGenitive(maxAgeDays)}`;
}

/**
 * Чего не хватает, чтобы отправить запись, — человеческим текстом, либо `null`,
 * если форма заполнена.
 *
 * Порядок вопросов тот же, что у сервера, и он существен: сначала «есть ли
 * вообще документ» (вид и номер), потом «когда он выдан», потом «удостоверяет
 * ли он то, ради чего заведён» (группа по ЭБ). Назвать срок документа, у
 * которого нет номера, значило бы отправить человека исправлять не то.
 *
 * `today` — календарный день того, кто смотрит, `ГГГГ-ММ-ДД`. Сервер считает
 * своё «сегодня» по времени Казахстана, и около полуночи они могут разойтись
 * на сутки; спор решается в пользу сервера — его отказ виден рядом с формой.
 */
export function entryRefusal(
  draft: ClearanceEntryDraft,
  type: ClearanceType | null,
  maxAgeDays: number,
  today: string,
): string | null {
  if (type === null) return 'Выберите вид допуска.';
  return fieldsRefusal(draft, type.category, maxAgeDays, today);
}

/**
 * Чего не хватает в полях самого документа — общая часть ввода и исправления
 * (issue #525).
 *
 * Правила у них одни и те же, и они одни и те же на сервере
 * (`clearance-entry-rules.ts` зовётся и из `create`, и из `update`). Второй
 * набор правил для исправления означал бы, что опечатку можно вписать правкой,
 * а вводом нельзя.
 */
function fieldsRefusal(
  draft: ClearanceEntryDraft,
  category: ClearanceCategory | undefined,
  maxAgeDays: number,
  today: string,
  windowApplies = true,
): string | null {
  if (draft.documentNumber.trim().length === 0) {
    return 'Укажите номер документа-основания: без номера запись не говорит, какой документ она удостоверяет.';
  }
  if (!DATE_RE.test(draft.documentDate) || Number.isNaN(toUtc(draft.documentDate))) {
    return 'Укажите дату документа.';
  }
  const ageDays = Math.round((toUtc(today) - toUtc(draft.documentDate)) / MS_PER_DAY);
  if (ageDays < 0) return 'Дата документа в будущем: документа, которого ещё нет, допуск не даёт.';
  if (windowApplies && ageDays > maxAgeDays) {
    return maxAgeDays === 0
      ? 'Документ вносится в день выдачи — так настроено в организации.'
      : `Документ старше ${daysGenitive(maxAgeDays)} — так настроено в организации.`;
  }
  if (draft.expiresAt.length > 0 && (!DATE_RE.test(draft.expiresAt) || Number.isNaN(toUtc(draft.expiresAt)))) {
    return 'Укажите срок действия датой или оставьте поле пустым, если допуск бессрочный.';
  }
  if (isElectricalCategory(category) && draft.electricalSafetyGroup.trim().length === 0) {
    return 'Укажите группу по электробезопасности: номер группы — это и есть содержание такого документа.';
  }
  if (draft.issuedBy.trim().length === 0) return 'Укажите, кто выдал документ.';
  return null;
}

/**
 * Чего не хватает, чтобы сохранить ИСПРАВЛЕНИЕ записи (issue #525).
 *
 * Причина спрашивается первой — тем же порядком, что у сервера: он бракует
 * запись без причины раньше, чем смотрит на её поля. Исправляют уже
 * удостоверенный документ, и журнал обязан сказать, почему его исправили;
 * исправление без причины ничем не отличается от подмены записи.
 *
 * Категория берётся из самой записи, а не из выбранного вида: вид могли
 * отключить, а группа в документе от этого не пропала.
 *
 * Issue #537: окно дат проверяется, только если дату документа меняют —
 * `originalDocumentDate` — дата в записи до исправления. Исправление номера у
 * документа, внесённого полгода назад, не делает его «старше окна».
 */
export function correctionRefusal(
  draft: ClearanceEntryDraft,
  category: ClearanceCategory | undefined,
  maxAgeDays: number,
  today: string,
  reason: string,
  originalDocumentDate?: string | null,
): string | null {
  if (reason.trim().length === 0) {
    return 'Укажите причину исправления: журнал обязан сказать, почему запись изменили.';
  }
  const dateChanged = originalDocumentDate === undefined || draft.documentDate !== (originalDocumentDate ?? '').slice(0, 10);
  return fieldsRefusal(draft, category, maxAgeDays, today, dateChanged);
}

/**
 * Черновик дозаполнения основания (issue #537): номер, дата документа и
 * причина — всё пустое, как в бумаге, которую только что нашли.
 */
export type BasisCompletionDraft = {
  documentNumber: string;
  documentDate: string;
  reason: string;
};

export function emptyBasisDraft(): BasisCompletionDraft {
  return { documentNumber: '', documentDate: '', reason: '' };
}

/** `ГГГГ-ММ-ДД` → `ДД.ММ.ГГГГ`. */
function dayRu(value: string): string {
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${day}.${month}.${year}`;
}

/** Подсказка под датой документа в форме дозаполнения. */
export function basisDateHint(validFrom: string): string {
  return `не позже начала действия допуска (${dayRu(validFrom)})`;
}

/**
 * Чего не хватает, чтобы дозаполнить основание (issue #537), — либо `null`.
 *
 * Повторение правил сервера (`clearanceBasisCompletionRefusal`), не строже
 * его. Окна дат организации здесь НЕТ: документ, который вписывают задним
 * числом, почти всегда старше него. Вместо окна — дата не в будущем и не
 * позже начала действия допуска (`validFrom`).
 */
export function basisCompletionRefusal(
  draft: BasisCompletionDraft,
  validFrom: string,
  today: string,
): string | null {
  if (draft.reason.trim().length === 0) {
    return 'Укажите причину: журнал обязан сказать, откуда взялись номер и дата документа.';
  }
  if (draft.documentNumber.trim().length === 0) {
    return 'Укажите номер документа-основания: номер и дата вписываются вместе.';
  }
  if (!DATE_RE.test(draft.documentDate) || Number.isNaN(toUtc(draft.documentDate))) {
    return 'Укажите дату документа: номер и дата вписываются вместе.';
  }
  if (toUtc(draft.documentDate) > toUtc(today)) {
    return 'Дата документа в будущем: документа, которого ещё нет, допуск не даёт.';
  }
  if (toUtc(draft.documentDate) > toUtc(validFrom.slice(0, 10))) {
    return `Дата документа позже начала действия допуска (${dayRu(validFrom)}): допуск не может действовать раньше, чем выдан документ.`;
  }
  return null;
}

/** Тело запроса на дозаполнение основания. */
export function basisCompletionDto(draft: BasisCompletionDraft): CompleteClearanceBasisDto {
  return {
    documentNumber: draft.documentNumber.trim(),
    documentDate: draft.documentDate,
    reason: draft.reason.trim(),
  };
}

/**
 * Предупреждение перед аннулированием — слово в слово то, что решил Штаб
 * (issue #525). Не «вы уверены?»: человеку называют последствие и то, что оно
 * необратимо.
 */
export const ANNULMENT_WARNING =
  'Запись перестанет давать допуск. Отменить аннулирование нельзя — ошибочную запись нужно будет ввести заново.';

/** Чего не хватает, чтобы аннулировать запись. Причина обязательна. */
export function annulmentRefusal(reason: string): string | null {
  return reason.trim().length === 0
    ? 'Укажите причину аннулирования: запись перестаёт давать допуск, и журнал обязан сказать, почему.'
    : null;
}

/**
 * Тело запроса на ввод записи — та же схема, которой его шлёт телефон
 * (`CreateClearanceSchema`).
 *
 * `issuingPerson` не отправляется: выдающим сервер поставит того, кто вносит
 * запись. Второй способ назвать выдающего в панели не заводится — в телефоне
 * его тоже нет (#205).
 */
export function entryDto(
  draft: ClearanceEntryDraft,
  employeeNumber: string,
  type: ClearanceType | null,
): CreateClearanceDto {
  // Пустая строка у `Number` даёт ноль, а не «ничего»: без этой проверки у
  // допуска по ЭБ без выбранной группы уходила бы группа «0».
  const chosen = draft.electricalSafetyGroup.trim();
  const group = Number(chosen);
  const notes = draft.notes.trim();
  return {
    employeeNumber,
    clearanceTypeId: draft.clearanceTypeId ?? '',
    documentNumber: draft.documentNumber.trim(),
    documentDate: draft.documentDate,
    ...(draft.expiresAt.length > 0 ? { expiresAt: draft.expiresAt } : {}),
    issuedBy: draft.issuedBy.trim(),
    ...(isElectricalType(type) && chosen.length > 0 && Number.isInteger(group)
      ? { electricalSafetyGroup: group }
      : {}),
    ...(notes.length > 0 ? { notes } : {}),
  };
}

/**
 * Тело запроса на исправление записи (issue #525).
 *
 * ОТПРАВЛЯЮТСЯ ВСЕ ПОЛЯ, а не только изменённые. Форма исправления показывает
 * запись целиком, и то, что на ней стоит, и должно оказаться в записи:
 * очищенное поле обязано очиститься, а не остаться прежним оттого, что панель
 * решила его «не менять». Что именно изменилось, считает сервер, сравнивая
 * строку до и после, — и это он же записывает в журнал строками «было → стало».
 *
 * Вида допуска и работника в теле нет: исправлением их не меняют.
 */
export function updateDto(
  draft: ClearanceEntryDraft,
  category: ClearanceCategory | undefined,
  reason: string,
): UpdateClearanceDto {
  const chosen = draft.electricalSafetyGroup.trim();
  const group = Number(chosen);
  const notes = draft.notes.trim();
  return {
    documentNumber: draft.documentNumber.trim(),
    documentDate: draft.documentDate,
    expiresAt: draft.expiresAt.length > 0 ? draft.expiresAt : null,
    issuedBy: draft.issuedBy.trim(),
    // Пустая строка у `Number` даёт ноль, а не «ничего», — та же ловушка, что
    // и при вводе. Группа не у того вида и группа «0» одинаково неверны.
    electricalSafetyGroup:
      isElectricalCategory(category) && chosen.length > 0 && Number.isInteger(group) ? group : null,
    notes: notes.length > 0 ? notes : null,
    reason: reason.trim(),
  };
}

/**
 * Подсказка при ручном вводе инструктажа (issue #543). Первичный, повторный и
 * внеплановый проводит мастер в приложении, и допуск создаёт запись журнала;
 * руками вносятся только старые данные.
 */
export const MANUAL_BRIEFING_NOTE =
  'Первичный, повторный и внеплановый инструктаж проводит мастер в приложении — допуск создаётся сам. Здесь вносятся только инструктажи, проведённые до перехода на приложение; запись будет помечена «внесено вручную».';
